import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { Printer, X, Search, Trash2, Calendar as CalendarIcon, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Slide-in right drawer for POS order history — a fuller-featured alternative
 * to the compact "Recent Orders" panel on the main POS screen. Supports:
 *   - date range filter (from/to)
 *   - text search (invoice number, table, notes)
 *   - status filter (paid / void)
 *   - re-print via existing /pos/orders/{id}/invoice endpoint
 *   - void with confirmation (admin only)
 *
 * Fetches from GET /pos/orders and lazily paginates by increasing `limit`.
 */
export default function PosHistoryDrawer({ outlet, onReprint, onClose, canVoid = false }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [status, setStatus] = useState("");
    const [query, setQuery] = useState("");
    const [limit, setLimit] = useState(50);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (outlet) params.set("outlet", outlet);
            if (from) params.set("date_from", from);
            if (to) params.set("date_to", to);
            if (status) params.set("status", status);
            params.set("limit", String(limit));
            const { data } = await api.get(`/pos/orders?${params.toString()}`);
            setRows(data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ngarkimi dështoi");
        } finally { setLoading(false); }
    };

    // Reload when any filter (except query) or limit changes.
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [outlet, from, to, status, limit]);

    const filtered = rows.filter((o) => {
        if (!query.trim()) return true;
        const q = query.toLowerCase();
        return (
            (o.invoice_number || "").toLowerCase().includes(q) ||
            (o.table_number || "").toString().toLowerCase().includes(q) ||
            (o.notes || "").toLowerCase().includes(q) ||
            (o.items || []).some((it) => (it.name || "").toLowerCase().includes(q))
        );
    });

    const total = filtered.reduce((s, o) => s + (Number(o.total) || 0), 0);

    const doReprint = async (id) => {
        try {
            const { data } = await api.get(`/pos/orders/${id}/invoice`);
            onReprint(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const doVoid = async (id) => {
        if (!window.confirm(t("pos.void_confirm", { defaultValue: "Anulo këtë faturë?" }))) return;
        try {
            await api.post(`/pos/orders/${id}/void`);
            toast.success(t("pos.voided", { defaultValue: "Fatura u anulua" }));
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const reset = () => { setFrom(""); setTo(""); setStatus(""); setQuery(""); setLimit(50); };

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="pos-history-drawer">
            {/* Backdrop */}
            <div className="flex-1 bg-black/40" onClick={onClose} />
            {/* Drawer */}
            <aside className="flex h-full w-full max-w-2xl flex-col border-l border-border bg-background shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
                    <div>
                        <div className="text-sm font-semibold tracking-tight text-foreground">
                            {t("pos.history_title", { defaultValue: "Historiku i faturave" })}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            {filtered.length} {t("pos.history_count", { defaultValue: "fatura" })} · {formatCurrency(total)}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="pos-history-close">
                        <X size={16} />
                    </button>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-2 gap-2 border-b border-border bg-muted/30 p-3 md:grid-cols-4">
                    <div className="relative col-span-2 md:col-span-4">
                        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t("pos.history_search", { defaultValue: "Kërko me faturë, tavolinë, artikull…" })}
                            className="akm-input h-8 w-full pl-6"
                            data-testid="pos-history-search"
                        />
                    </div>
                    <label className="text-[11px]">
                        <span className="mb-0.5 block text-muted-foreground">{t("pos.history_from", { defaultValue: "Nga" })}</span>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="akm-input h-8 w-full" data-testid="pos-history-from" />
                    </label>
                    <label className="text-[11px]">
                        <span className="mb-0.5 block text-muted-foreground">{t("pos.history_to", { defaultValue: "Deri" })}</span>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="akm-input h-8 w-full" data-testid="pos-history-to" />
                    </label>
                    <label className="text-[11px]">
                        <span className="mb-0.5 block text-muted-foreground">{t("pos.history_status", { defaultValue: "Statusi" })}</span>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="akm-input h-8 w-full">
                            <option value="">— {t("pos.history_all", { defaultValue: "Të gjitha" })} —</option>
                            <option value="paid">Paid</option>
                            <option value="void">Void</option>
                            <option value="open">Open</option>
                        </select>
                    </label>
                    <button onClick={reset} className="akm-btn-ghost text-[11px]" data-testid="pos-history-reset">
                        <RotateCcw size={11} /> {t("pos.history_reset", { defaultValue: "Pastro" })}
                    </button>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-3">
                    {loading ? (
                        <div className="py-10 text-center text-[12px] text-muted-foreground">…</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-10 text-center text-[12px] text-muted-foreground">
                            {t("pos.history_empty", { defaultValue: "Asnjë faturë nuk përputhet." })}
                        </div>
                    ) : (
                        <ul className="space-y-1.5">
                            {filtered.map((o) => (
                                <li key={o.id}
                                    className="rounded-md border border-border bg-card p-2.5 text-[12px]"
                                    data-testid={`pos-history-row-${o.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="font-mono text-[11px] font-semibold text-foreground">{o.invoice_number || o.id.slice(0, 8)}</span>
                                                <span className={`rounded px-1.5 py-0 text-[10px] font-semibold ${
                                                    o.status === "void" ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300" :
                                                    o.status === "paid" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" :
                                                    "bg-muted text-muted-foreground"
                                                }`}>{o.status || "open"}</span>
                                                {o.table_number && <span className="text-muted-foreground">· T{o.table_number}</span>}
                                                {o.reservation_id && <span className="rounded bg-amber-50 px-1 text-[10px] text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">Room charge</span>}
                                            </div>
                                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                                <CalendarIcon size={10} />
                                                <span>{formatDateTime(o.created_at)}</span>
                                                <span>·</span>
                                                <span>{o.items.length} {t("pos.items", { defaultValue: "artikuj" })}</span>
                                                {o.created_by_name && <><span>·</span><span>{o.created_by_name}</span></>}
                                            </div>
                                        </div>
                                        <div className="ml-2 flex items-center gap-2">
                                            <span className={`text-[13px] font-semibold tabular-nums ${o.status === "void" ? "text-muted-foreground line-through" : "text-foreground"}`}>
                                                {formatCurrency(o.total)}
                                            </span>
                                            <button onClick={() => doReprint(o.id)} className="text-muted-foreground hover:text-foreground" title={t("pos.reprint", { defaultValue: "Ri-printo" })} data-testid={`pos-history-reprint-${o.id}`}>
                                                <Printer size={13} />
                                            </button>
                                            {canVoid && o.status !== "void" && (
                                                <button onClick={() => doVoid(o.id)} className="text-red-600 hover:text-red-700" title={t("pos.void", { defaultValue: "Anulo" })} data-testid={`pos-history-void-${o.id}`}>
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Item detail — collapsible on click */}
                                    <details className="mt-1.5">
                                        <summary className="cursor-pointer text-[10.5px] text-muted-foreground hover:text-foreground">
                                            {t("pos.history_details", { defaultValue: "Shiko artikujt" })}
                                        </summary>
                                        <ul className="mt-1 divide-y divide-border rounded-md bg-muted/30 px-2">
                                            {o.items.map((it, i) => (
                                                <li key={i} className="flex items-center justify-between py-1 text-[11px]">
                                                    <span className="truncate">
                                                        <span className="text-muted-foreground">×{it.qty}</span> {it.name}
                                                    </span>
                                                    <span className="tabular-nums">{formatCurrency(Number(it.price) * Number(it.qty))}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                </li>
                            ))}
                            {rows.length >= limit && (
                                <li>
                                    <button onClick={() => setLimit(limit + 50)} className="akm-btn-ghost w-full text-[11px]" data-testid="pos-history-load-more">
                                        {t("pos.history_load_more", { defaultValue: "Ngarko më shumë" })}
                                    </button>
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            </aside>
        </div>
    );
}
